// Brand registry for the customer portal.
//
// REACT_APP_BRAND selects one of ./brands/*.js at build time (CRA inlines env
// vars). There is deliberately no default: a build that does not name its
// brand fails immediately instead of shipping another company's identity.
//
// Colours are exposed two ways:
//   - CSS custom properties on <html>:  --brand-accent, --brand-accent-rgb, ...
//     (set by applyBrand() before React renders; SCSS uses var(--brand-*)).
//   - Tailwind colour names configured in public/index.html that read those
//     variables:  bg-brand, text-brand-text, text-brand-on, bg-brand/20, ...
// Never hard-code a brand colour in a component again; add a token here.
import upwardor from "./brands/upwardor";
import encore from "./brands/encore";

const registry = { upwardor, encore };

const key = String(process.env.REACT_APP_BRAND || "").trim();
if (!key) {
  throw new Error(
    "[brand] REACT_APP_BRAND is not set. Add REACT_APP_BRAND=<upwardor|encore> to the .env used for this build."
  );
}
if (!registry[key]) {
  throw new Error(
    `[brand] Unknown REACT_APP_BRAND "${key}". Known brands: ${Object.keys(registry).join(", ")}.`
  );
}

export const brand = Object.freeze(registry[key]);

/** Resolve a /assets/img icon through the brand's override table. */
export const brandAsset = (file) =>
  brand.assets.icons[file] || `/assets/img/${file}`;

const kebab = (s) => s.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

/** "#rrggbb" -> "r g b" (space separated, for rgb(var(--x-rgb) / alpha)). */
const hexToRgb = (hex) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};

/** Apply brand identity to the document. Call once, before rendering. */
export const applyBrand = () => {
  const root = document.documentElement;
  root.setAttribute("data-brand", brand.key);

  Object.entries(brand.theme).forEach(([name, value]) => {
    root.style.setProperty(`--brand-${kebab(name)}`, value);
    const rgb = hexToRgb(value);
    if (rgb) root.style.setProperty(`--brand-${kebab(name)}-rgb`, rgb);
  });

  document.title = brand.title;

  const setLink = (rel, href, type) => {
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement("link");
      el.rel = rel;
      document.head.appendChild(el);
    }
    el.href = href;
    if (type) el.type = type;
  };
  setLink(
    "icon",
    brand.assets.favicon,
    brand.assets.favicon.endsWith(".svg") ? "image/svg+xml" : ""
  );
  setLink("apple-touch-icon", brand.assets.favicon);
  setLink("manifest", brand.assets.manifest);

  brand.fonts.forEach((href) => {
    if (document.querySelector(`link[href="${href}"]`)) return;
    const el = document.createElement("link");
    el.rel = "stylesheet";
    el.href = href;
    document.head.appendChild(el);
  });
};

export default brand;
